import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, AlertCircle, CheckCircle, Upload, X, Lock, Calendar, Globe, Shield } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DIVING_LEVELS = [
  "Beginner / Not Certified",
  "Open Water Diver",
  "Advanced Open Water",
  "Rescue Diver",
  "Divemaster",
  "Instructor"
];

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const returnUrl = searchParams.get('return') || '';
  const mode = searchParams.get('mode');
  const isReviewMode = mode === 'review';

  const [formData, setFormData] = useState({
    name: "", username: "", email: "", password: "", confirmPassword: "",
    country: "", diving_level: "", age: "", profile_pic: "",
    acceptTerms: false, acceptPrivacy: false,
    dateOfBirth: "", parentEmail: "", parentConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenMessage, setTokenMessage] = useState("");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [invitationType, setInvitationType] = useState("member");
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState("");
  const [uploadingPic, setUploadingPic] = useState(false);

  const isDiveCenterInvite = invitationType === "dive_center";

  useEffect(() => {
    const validateToken = async () => {
      if (isReviewMode && inviteToken) {
        setTokenValid(true);
        setTokenMessage("Welcome! Complete your registration to join the community.");
        setTokenValidating(false);
        return;
      }
      if (!inviteToken) {
        setTokenValid(false);
        setTokenMessage("This is an invite-only community. Please use your invitation link to register.");
        setTokenValidating(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/validate-invite/${inviteToken}`);
        if (response.data.valid) {
          setTokenValid(true);
          setInvitedEmail(response.data.email);
          setInvitationType(response.data.invitation_type || "member");
          setFormData(prev => ({ ...prev, email: response.data.email }));
          setTokenMessage("Valid invitation! Please complete your registration.");
        } else {
          setTokenValid(false);
          setTokenMessage(response.data.message || "Invalid invitation link");
        }
      } catch {
        setTokenValid(false);
        setTokenMessage("Failed to validate invitation. Please try again or contact support.");
      } finally {
        setTokenValidating(false);
      }
    };
    validateToken();
  }, [inviteToken, isReviewMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image size must be less than 5MB"); return; }
    setProfilePicFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeProfilePic = () => {
    setProfilePicFile(null);
    setProfilePicPreview("");
    setFormData(prev => ({ ...prev, profile_pic: "" }));
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.username || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields"); return;
    }
    if (!formData.dateOfBirth) { toast.error("Please enter your date of birth"); return; }
    const userAge = calculateAge(formData.dateOfBirth);
    if (userAge < 13) { toast.error("You must be at least 13 years old to register"); return; }
    if (userAge < 18) {
      if (!formData.parentEmail) { toast.error("Parental email is required for users under 18"); return; }
      if (!formData.parentConsent) { toast.error("Parental consent is required for users under 18"); return; }
    }
    if (formData.password !== formData.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (formData.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }

    setLoading(true);
    try {
      let profilePicUrl = "";
      if (profilePicFile) {
        try {
          setUploadingPic(true);
          const formDataPic = new FormData();
          formDataPic.append('file', profilePicFile);
          const uploadResponse = await axios.post(`${API}/upload/profile-picture-guest`, formDataPic, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          profilePicUrl = uploadResponse.data.image_url;
        } catch (uploadError) {
          console.error("Profile picture upload error:", uploadError);
          toast.error("Failed to upload profile picture, continuing without it");
        } finally {
          setUploadingPic(false);
        }
      }

      const userAgeValue = calculateAge(formData.dateOfBirth);
      const response = isReviewMode
        ? await axios.post(`${API}/auth/register-review`, {
            name: formData.name, username: formData.username, email: formData.email,
            password: formData.password, country: formData.country || undefined,
            diving_level: formData.diving_level || undefined,
            age: userAgeValue,
            profile_pic: profilePicUrl || undefined, review_token: inviteToken
          })
        : await axios.post(`${API}/auth/register`, {
            name: formData.name, username: formData.username, email: formData.email,
            password: formData.password, country: formData.country || undefined,
            diving_level: formData.diving_level || undefined,
            age: userAgeValue,
            profile_pic: profilePicUrl || undefined, role: "member", invitation_token: inviteToken
          });

      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("token", response.data.token);
      window.dispatchEvent(new Event("userLogin"));
      toast.success("Welcome to the George Dives community!", { duration: 5000 });

      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate(`/profile/${response.data.user.id}`);
      }
    } catch (error) {
      console.error("Error registering:", error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Registration failed");
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "Invalid invitation token");
      } else {
        toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (tokenValidating) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <section className="pt-32 pb-8 bg-gradient-to-br from-cyan-500 to-blue-600">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Join the Community</h1>
            <p className="text-white/80">Validating your invitation...</p>
          </div>
        </section>
        <div className="container mx-auto px-4 py-16 max-w-2xl flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
        </div>
        <FloatingUserMenu />
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <section className="pt-32 pb-8 bg-gradient-to-br from-cyan-500 to-blue-600">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Invitation Required</h1>
            <p className="text-white/80">{tokenMessage}</p>
          </div>
        </section>
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Card>
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                <p className="font-semibold text-amber-800 mb-3">George Dives is an Invite-Only Community</p>
                <p className="text-gray-600 text-sm mb-3">
                  We&apos;re building an exclusive space for educators, divers, and enthusiasts.
                  Registration is only available to those who have received a personal invitation.
                </p>
                <p className="text-gray-600 text-sm">
                  If you believe you should have access, please contact{" "}
                  <a href="mailto:info@georgedives.com" className="text-cyan-600 underline hover:text-cyan-700">
                    info@georgedives.com
                  </a>
                </p>
              </div>
              <div className="flex justify-center">
                <Button onClick={() => navigate('/')} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="return-home-btn">
                  Return to Homepage
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <FloatingUserMenu />
        <Footer />
      </div>
    );
  }

  // Main registration form
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Header */}
      <section className="pt-32 pb-8 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Join the Community</h1>
          <p className="text-white/80">Create your account to share dive site observations and connect with fellow divers</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Success banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3 mb-6">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 text-sm">
            {isReviewMode ? (
              <><strong>Welcome!</strong> Create your account to join the George Dives community and submit your review.</>
            ) : (
              <><strong>Invitation Verified!</strong> Welcome to the George Dives community. Complete your registration below.</>
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <UserPlus className="w-5 h-5 text-cyan-600" />
                Account Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleInputChange}
                    placeholder="John Doe" required data-testid="register-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input id="username" name="username" value={formData.username} onChange={handleInputChange}
                    placeholder="johndoe" required data-testid="register-username" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *{!isReviewMode && " (from invitation)"}</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange}
                  placeholder="john@example.com" required readOnly={!isReviewMode}
                  className={!isReviewMode ? "bg-gray-50 cursor-not-allowed" : ""}
                  data-testid="register-email" />
                {!isReviewMode && (
                  <p className="text-xs text-gray-500">This email is linked to your invitation and cannot be changed.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Lock className="w-5 h-5 text-cyan-600" />
                Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input id="password" name="password" type="password" value={formData.password}
                    onChange={handleInputChange} placeholder="At least 6 characters" required
                    data-testid="register-password" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword}
                    onChange={handleInputChange} placeholder="Repeat your password" required
                    data-testid="register-confirm-password" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Age Verification */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="w-5 h-5 text-cyan-600" />
                Age Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                <Input id="dateOfBirth" name="dateOfBirth" type="date"
                  max={new Date().toISOString().split('T')[0]}
                  value={formData.dateOfBirth} onChange={handleInputChange} required
                  data-testid="register-dob" />
                <p className="text-xs text-gray-500">Required for age verification and legal compliance</p>
              </div>

              {/* Parental consent for under 18 */}
              {formData.dateOfBirth && calculateAge(formData.dateOfBirth) < 18 && calculateAge(formData.dateOfBirth) >= 13 && (
                <div className="space-y-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    <p className="font-semibold">Parental Consent Required (Under 18)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="parentEmail">Parent/Guardian Email *</Label>
                    <Input id="parentEmail" name="parentEmail" type="email" placeholder="parent@example.com"
                      value={formData.parentEmail} onChange={handleInputChange} required />
                    <p className="text-xs text-gray-500">We&apos;ll send a consent confirmation to this email</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" id="parentConsent" checked={formData.parentConsent}
                      onChange={(e) => setFormData(prev => ({ ...prev, parentConsent: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500" required />
                    <Label htmlFor="parentConsent" className="text-gray-600 text-sm cursor-pointer">
                      I confirm that I am the parent/legal guardian of the user registering, and I consent to their
                      participation in diving activities and data collection as outlined in the{" "}
                      <a href="/privacy" target="_blank" className="text-cyan-600 hover:underline">Privacy Policy</a>.
                    </Label>
                  </div>
                </div>
              )}

              {/* Too young */}
              {formData.dateOfBirth && calculateAge(formData.dateOfBirth) < 13 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    <p className="font-semibold">You must be at least 13 years old to register</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dive Center Notice */}
          {isDiveCenterInvite && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-cyan-800">
                <CheckCircle className="w-5 h-5" />
                <p className="font-semibold">Dive Center Account</p>
              </div>
              <p className="text-sm text-cyan-700 mt-2">
                You&apos;re registering as a dive center. Your profile will be linked to your dive center&apos;s information,
                which can be managed after registration.
              </p>
            </div>
          )}

          {/* Optional Information */}
          {!isDiveCenterInvite && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Globe className="w-5 h-5 text-cyan-600" />
                  Additional Info
                  <span className="text-xs font-normal text-gray-400 ml-1">(optional, can be filled later)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input id="country" name="country" value={formData.country} onChange={handleInputChange}
                      placeholder="e.g., USA" data-testid="register-country" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="diving_level">Diving Level</Label>
                  <Select value={formData.diving_level}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, diving_level: value }))}>
                    <SelectTrigger data-testid="register-diving-level">
                      <SelectValue placeholder="Select your diving level..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {DIVING_LEVELS.map((level) => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Profile Picture</Label>
                  {!profilePicPreview ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-cyan-400 transition-colors cursor-pointer"
                         onClick={() => document.getElementById('profile_pic').click()}>
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-1">Click to upload profile picture</p>
                      <p className="text-xs text-gray-400">PNG, JPG up to 5MB</p>
                      <Input id="profile_pic" name="profile_pic" type="file" accept="image/*"
                        onChange={handleProfilePicChange} className="hidden" />
                    </div>
                  ) : (
                    <div className="relative flex justify-center">
                      <img src={profilePicPreview} alt="Profile preview"
                        className="w-32 h-32 rounded-full object-cover border-4 border-cyan-200" />
                      <button type="button" onClick={removeProfilePic}
                        className="absolute top-0 ml-24 bg-red-500 hover:bg-red-600 text-white rounded-full p-1">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Legal Agreement */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="w-5 h-5 text-cyan-600" />
                Legal Agreement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <input type="checkbox" id="acceptTerms" checked={formData.acceptTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
                  required className="mt-1 w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500" />
                <label htmlFor="acceptTerms" className="text-sm text-gray-600">
                  I have read and agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700 underline">
                    Terms and Conditions
                  </a> *
                </label>
              </div>
              <div className="flex items-start gap-3">
                <input type="checkbox" id="acceptPrivacy" checked={formData.acceptPrivacy}
                  onChange={(e) => setFormData(prev => ({ ...prev, acceptPrivacy: e.target.checked }))}
                  required className="mt-1 w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500" />
                <label htmlFor="acceptPrivacy" className="text-sm text-gray-600">
                  I have read and agree to the{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700 underline">
                    Privacy Policy
                  </a> *
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button type="submit" disabled={loading || uploadingPic || !formData.acceptTerms || !formData.acceptPrivacy}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white text-lg py-6"
            data-testid="register-submit-btn">
            {loading ? "Creating Account..." : uploadingPic ? "Uploading Picture..." : "Create Account"}
          </Button>

          <div className="text-center text-sm text-gray-500 pb-4">
            Already have an account?{" "}
            <button type="button" onClick={() => navigate('/account')}
              className="text-cyan-600 hover:text-cyan-700 underline">
              Login here
            </button>
          </div>
        </form>
      </div>

      <FloatingUserMenu />
      <Footer />
    </div>
  );
};

export default Register;
